import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as BookingService from "_services/booking-service";
import { format } from "date-fns";
import { PRMCodes } from "_utils/constants";
import { hasEquipment } from "_utils/equipment";

function formatDate(dateString) {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = `0${date.getUTCMonth() + 1}`.slice(-2);
  const day = `0${date.getUTCDate()}`.slice(-2);
  const hours = `0${date.getUTCHours()}`.slice(-2);
  const minutes = `0${date.getUTCMinutes()}`.slice(-2);
  return `${year}-${month}-${day} (${hours}:${minutes})`;
}

const initialState = {
  arrivals: [],
  count: 0,
  filters: [],
  skip: 0,
  limit: 100,
  countStatus: "idle",
  statusObj: {},
  status: "idle",
  error: null,
};

// get all bookings
export const getArrivals = createAsyncThunk(
  "arrivals/getArrivals",
  async (params, signal) => {
    return BookingService.getArrivals(params, signal);
  }
);

export const getArrivalsWithSearch = createAsyncThunk(
  "arrivals/getArrivalsWithSearch",
  async (params, signal) => {
    return BookingService.getArrivalsWithSearch(params, signal);
  }
);
// get count for list
export const getArrivalsCount = createAsyncThunk(
  "arrivals/getArrivalsCount",
  async (params, signal) => {
    return BookingService.getArrivalsOrDeparturesCount(params, signal);
  }
);


const arrivalsSlice = createSlice({
  name: "arrivals",
  initialState,
  reducers: {
    reset: (state) => initialState,
    resetSkip: (state) => {
      return {
        ...state,
        skip: 0
      }
    },
    IncreaseArrivalSkip: (state, action) => {
      return {
        ...state,
        skip: state.skip + action.payload,
      }
    },

    DecreaseArrivalSkip: (state, action) => {
      return {
        ...state,
        skip: state.skip - action.payload,
      }
    },

    setFilters: (state, action) => {
      return {
        ...state,
        filters: action.payload,
      }
    },
  },
  extraReducers: {
    [getArrivalsWithSearch.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getArrivals: 'loading' },
      }
    },
    [getArrivalsWithSearch.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getArrivals: 'succeeded' },
        arrivals: action.payload,
        count: action.payload.length,
      }
    },
    [getArrivalsWithSearch.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: { ...state.statusObj, getArrivals: 'failed' },
        error: !action.meta.aborted ? action.error.message : state.error,
      }
    },
    [getArrivals.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getArrivals: 'loading' },
      }
    },
    [getArrivals.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getArrivals: 'succeeded' },
        arrivals: action.payload,
      }
    },
    [getArrivals.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: { ...state.statusObj, getArrivals: 'failed' },
        error: !action.meta.aborted ? action.error.message : state.error,
      }
    },
    [getArrivalsCount.pending]: (state, action) => {
      return {
        ...state,
        statusObj: { ...state.statusObj, getArrivalsCount: 'loading' },
        countStatus: "loading",
      }
    },
    [getArrivalsCount.fulfilled]: (state, action) => {
      return {
        ...state,
        statusObj: { ...state.statusObj, getArrivalsCount: 'succeeded' },
        countStatus: "succeeded",
        count: action.payload.totalArrivals,
      }
    },
    [getArrivalsCount.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: { ...state.statusObj, getArrivalsCount: 'failed' },
        countStatus: !action.meta.aborted ? "failed" : state.countStatus,
      }
    },

  },
});

export const arrivalsList = (filter) => (state) => {
  // if null or undefined return empty
  if (state.arrivals.arrivals == null) {
    return [];
  }
  if (state.arrivals.arrivals == undefined) {
    return [];
  }

  // bookings exsist, create new list object for table
  let list = [];

  // used to capitalize and remove initals from string
  function processName(first, second) {
    // MR MISS MRS MS
    if (first.slice(first.length - 3) == "MRS") {
      return (
        "Mrs " +
        capitalize(first.substring(0, first.length - 3)) +
        " " +
        capitalize(second)
      );
    } else if (first.slice(first.length - 2) == "MS") {
      return (
        "Ms " +
        capitalize(first.substring(0, first.length - 2)) +
        " " +
        capitalize(second)
      );
    } else if (first.slice(first.length - 2) == "MR") {
      return (
        "Mr " +
        capitalize(first.substring(0, first.length - 2)) +
        " " +
        capitalize(second)
      );
    } else if (first.slice(first.length - 4) == "MISS") {
      return (
        "Miss " +
        capitalize(first.substring(0, first.length - 4)) +
        " " +
        capitalize(second)
      );
    } else {
      return capitalize(first) + " " + capitalize(second);
    }
  }

  function processAssistance(assistance) {
    let result = [];
    assistance.forEach((a) => {
      for (const r in PRMCodes) {
        let regex = PRMCodes[r];
        if (a && a.includes(regex)) {
          result.push(regex);
        }
      }
    });
    result = [...new Set(result)];
    return result;
  }

  // capitalize names //
  function capitalize(string) {
    if (string && string[0]) {
      return string[0].toUpperCase() + string.slice(1).toLowerCase();
    } else return string;
  }

  let arrivalsCount = 0;

  state.arrivals.arrivals.forEach((booking) => {
    if (booking != null) {
      arrivalsCount++;

      let fullName = booking.name.split(" ");

      list.push({
        count: arrivalsCount + state.arrivals.skip,
        id: booking._id,
        origin: booking.flightConfirmationDetails.origin || "",
        destination: booking.flightConfirmationDetails.destination || "",
        name: processName(fullName[0], fullName.slice(1).join(" ")),
        assistances: processAssistance(booking.assistance),
        equipment: hasEquipment(booking),
        date: booking.flightConfirmationDetails.departureDate,
        flightNumber:
          booking.flightConfirmationDetails.carrierCode +
          booking.flightConfirmationDetails.flightNumber,
        airport: booking.flightConfirmationDetails.destination,
        type: booking.userId == "SITA" ? "SITA" : "PRM Assist",
        flightTime: " ",
        contact: " ",
        dueTime: booking.dueTime ? booking.dueTime : "Not Available",
        bio:
          booking.userId == "SITA"
            ? booking.assistance.join("")
            : booking.bio
              ? booking.bio
              : "",
        arrivalTime: !isNaN(
          new Date(booking.flightConfirmationDetails.arrivalTime).getTime()
        )
          ? format(
            new Date(booking.flightConfirmationDetails.arrivalTime),
            "h:mm a"
          )
          : "N/A",
        departureTime: !isNaN(
          new Date(booking.flightConfirmationDetails.departureTime).getTime()
        )
          ? format(
            new Date(booking.flightConfirmationDetails.departureTime),
            "h:mm a"
          )
          : "N/A",
        dateCreated: formatDate(booking.dateCreated),
        //timePreadvised: (new Date(booking.dateCreated) - new Date(booking.flightConfirmationDetails.arrivalTime)) / (60 * 1000),
        leg1Assistance: booking.destinationAssistance ? 'Requested' : 'Not Requested',
        leg2Assistance: booking.originAssistance ? 'Requested' : 'Not Requested'

      });
    }
  });

  return list;
};

export const { reset, resetSkip, IncreaseArrivalSkip, DecreaseArrivalSkip, setFilters } = arrivalsSlice.actions;
export const selectArrivalStatus = (state) => state.arrivals.status;
export const selectArrivals = (state) => state.arrivals.arrivals;
export const selectArrivalsCount = (state) => state.arrivals.count;
export const selectArrivalsCountStatus = (state) => state.arrivals.countStatus;
export const selectArrivalsSkip = (state) => state.arrivals.skip;

export default arrivalsSlice.reducer;
